import { computed, ref } from "vue";
import { useModal } from "@/components/UI/Modal/useModal";
import {
  newOrderFormInputs,
  newOrderProductInputs,
  ORDER_TYPES,
} from "@/consts/newOrderFormInputs";
import { useOptionsStoreActions } from "@/hooks/store/useOptionsStore";
import { useSubmitOrder } from "@/hooks/useSubmitOrder";
import useToast from "@/hooks/useToast";
import { fileFromUrl } from "@/utils/fileFromUrl";
import { findOption } from "@/utils/optionValue";
import { checkRaw, collectOrderRequestBody } from "@/utils/orderUtils";
import {
  orderAdditionalInfoValidator,
  orderInfoValidator,
  orderProductsValidator,
} from "@/utils/orderValidator";
import { orderOptionsFromScene } from "@/utils/sceneOrderOptions";

const asFileField = (file) => [{ name: file.name, file }];

// A preview becomes either a paid quick order entry, or a quote the customer pays through
// the link in its e-mail. A quote's canvas is only sent to production once it is paid.
const [QUICK_ORDER, QUOTE] = ORDER_TYPES;
const SYNC_ORDER_TYPES = [QUICK_ORDER, QUOTE];

// Syncing a preview turns it into an HQ order. It is quick order entry (or a quote) with
// the preview's data filled in: the same fields, the same checks, the same request — plus
// products[i].pretty_preview_id, which tells the back end which order of the group the
// preview (and its canvas) belongs to.
export const useSyncPreviewOrder = (preview) => {
  const { close } = useModal();
  const { openToast } = useToast();
  const { submitOrder } = useSubmitOrder();
  const {
    canvasProductsOptions,
    productsOptions,
    loadCanvasProductsOptions,
    loadProductsOptions,
    loadSellersOptions,
    loadCountriesOptions,
    loadShippingServicesOptions,
    loadArtistTeamOptions,
  } = useOptionsStoreActions();

  const form = ref({
    ...newOrderFormInputs,
    name: preview.customer_name || null,
    email: preview.email || null,
    phone: preview.phone || null,
  });

  const product = ref({
    ...newOrderProductInputs,
    product: preview.product_id,
    qty: 1,
    ...orderOptionsFromScene(preview.canvas_scene),
  });

  const additionalInfo = ref({ shipping_cost: null, tax: null });
  // One wait for everything the form needs: its lists and the two photos.
  const isLoading = ref(true);
  // While the order is being put together and sent.
  const isSubmitting = ref(false);

  const canvasProduct = computed(() => findOption(canvasProductsOptions.value, preview.product_id));
  const productName = computed(() => canvasProduct.value?.label || `#${preview.product_id}`);
  const rawType = computed(() => checkRaw(form.value.type));
  const isQuote = computed(() => form.value.type === QUOTE.value);

  // allSettled: a list that fails has already been reported by the request layer, and the
  // form still opens without it instead of hanging on the loader.
  const loadLists = () =>
    Promise.allSettled([
      loadCanvasProductsOptions(),
      // The order checks read the order products to know whether photos are required.
      loadProductsOptions(),
      // The lists of the order form's main information.
      loadSellersOptions(),
      loadCountriesOptions(),
      loadShippingServicesOptions(),
      loadArtistTeamOptions(),
    ]);

  const loadPhotos = async () => {
    try {
      const [photo, snapshot] = await Promise.all([
        preview.photo_url ? fileFromUrl(preview.photo_url) : null,
        preview.preview_image_url ? fileFromUrl(preview.preview_image_url) : null,
      ]);
      if (photo) product.value.mainPhoto = asFileField(photo);
      if (snapshot) product.value.extraPhoto = asFileField(snapshot);
    } catch (error) {
      openToast("Could not load the preview photos for the order.", "danger");
    }
  };

  Promise.all([loadLists(), loadPhotos()]).then(() => {
    // The cost starts at the catalogue price, once the products are known.
    if (product.value.price == null && canvasProduct.value?.retail != null) {
      product.value.price = canvasProduct.value.retail;
    }
    isLoading.value = false;
  });

  const submit = async () => {
    if (!isQuote.value && !String(form.value.transactionId || "").trim()) {
      openToast("Transaction ID is required to sync.", "danger");
      return;
    }
    if (isLoading.value) {
      openToast("The photos are still loading. Please wait a moment.", "danger");
      return;
    }
    const valid =
      orderInfoValidator(form.value) &&
      orderProductsValidator([product.value], productsOptions, rawType.value) &&
      orderAdditionalInfoValidator(additionalInfo.value);
    if (!valid) return;

    isSubmitting.value = true;
    try {
      const order = await collectOrderRequestBody(
        { ...form.value, ...additionalInfo.value },
        [product.value],
        rawType.value
      );
      // The sync form orders exactly one item: the preview's own product.
      order.products[0].pretty_preview_id = preview.id;
      // Closed only once the order exists: a refused order leaves everything typed in
      // place. The modal shows its own loader meanwhile — the app's sits under it.
      if (await submitOrder(order, { showLoader: false })) close();
    } catch (error) {
      // collectOrderRequestBody reads the photos into the order, and a photo that
      // cannot be read stops it before anything is sent.
      openToast("Could not prepare the order. Please check the photos and try again.", "danger");
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    form,
    product,
    additionalInfo,
    productName,
    isLoading,
    isSubmitting,
    isQuote,
    SYNC_ORDER_TYPES,
    photoUrl: preview.photo_url || "",
    previewImageUrl: preview.preview_image_url || "",
    submit,
  };
};
